import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

interface XpProgressProps {
  readonly xpTotal: number
  readonly level: number
  readonly xpToNextLevel: number
  readonly progressPercent: number
  /** Anime le compteur d'XP au montage. Désactivable pour les tests et l'impression. */
  readonly animate?: boolean
}

const COUNT_UP_STEPS = 24
const COUNT_UP_INTERVAL_MS = 30

export function XpProgress({
  xpTotal,
  level,
  xpToNextLevel,
  progressPercent,
  animate = true,
}: XpProgressProps) {
  const { t } = useTranslation()
  const [counted, setCounted] = useState(0)

  // Sans animation, le total s'affiche directement : rien à stocker.
  const displayed = animate ? counted : xpTotal

  useEffect(() => {
    if (!animate) return

    const step = Math.max(1, Math.ceil(xpTotal / COUNT_UP_STEPS))
    let current = 0

    const timer = setInterval(() => {
      current += step
      setCounted(Math.min(current, xpTotal))

      if (current >= xpTotal) clearInterval(timer)
    }, COUNT_UP_INTERVAL_MS)

    return () => clearInterval(timer)
  }, [xpTotal, animate])

  return (
    <section
      aria-label={t('gamification.level', { level })}
      className="flex flex-col gap-3 rounded-3xl bg-surface-low p-6"
    >
      <div className="flex items-baseline justify-between gap-3">
        <p className="font-display text-lg font-semibold text-on-surface">
          {t('gamification.level', { level })}
        </p>
        <p className="font-display text-2xl font-bold tabular-nums text-secondary">
          {t('gamification.xp_total', { count: displayed })}
        </p>
      </div>

      <div
        role="progressbar"
        aria-valuenow={progressPercent}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={t('gamification.level', { level })}
        className="h-2.5 overflow-hidden rounded-full bg-surface-container"
      >
        <div
          className="h-full rounded-full bg-gradient-to-r from-secondary to-primary transition-[width] duration-700"
          style={{ width: `${progressPercent}%` }}
        />
      </div>

      <p className="text-xs text-on-surface-variant">
        {xpToNextLevel > 0
          ? t('gamification.xp_to_next', { count: xpToNextLevel, level: level + 1 })
          : t('gamification.max_level')}
      </p>
    </section>
  )
}
